import { createStyles } from '@mantine/core';

import { MARKET_CELL_WIDTH_MOBILE } from '../../constants/marketCell';

const CARD_PEEK = 40;
/** Three across the 1376px desktop carousel, per design. */
const CARDS_ON_DESKTOP = 3;
const CARD_GAP = 8;
/**
 * A flex item cannot render below its content, so the basis has to clear the widest card or the
 * cards render at their individual min-content widths — visibly uneven, with the last clipped.
 * A live card is the widest: its score column costs ~130px over a scheduled one, measured at
 * 435px with a three-digit score. Below the track needed for three, this simply shows two.
 */
const MIN_CARD_WIDTH = 435;

export const CAROUSEL_CARD_HEIGHT = 200;

/**
 * The Scroller lays children out in an inline-flex track, so a percentage width resolves against
 * a shrink-to-fit box. MarketsCarousel measures the track and publishes it here instead.
 */
export const TRACK_WIDTH_VAR = '--promoted-track-width';

/**
 * MarketCard sizes itself for a full-width column above `sm`. These custom properties are its
 * documented opt-out, letting three cards fit the carousel; unset, the card is unchanged.
 */
const COMPACT_CARD_VARS = {
  '--market-cell-min-width': `${MARKET_CELL_WIDTH_MOBILE}px`,
  '--market-cell-direction': 'column',
  '--market-cell-gap': '2px',
  '--market-cell-padding': '4px 6px',
  '--market-team-name-short-display': 'inline',
  '--market-team-name-full-display': 'none',
} as const;

export const useStyles = createStyles((theme) => ({
  carouselItem: {
    ...COMPACT_CARD_VARS,
    whiteSpace: 'normal',
    // Content differs between scheduled and live games, so stretch every card to the tallest
    // rather than letting each size to its own content and leaving ragged bottoms.
    display: 'flex',

    '& > a': {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
    },

    '& > a > *': {
      flex: 1,
    },
    flex: `0 0 max(calc((var(${TRACK_WIDTH_VAR}, 100%) - ${CARD_PEEK}px - ${
      (CARDS_ON_DESKTOP - 1) * CARD_GAP
    }px) / ${CARDS_ON_DESKTOP}), ${MIN_CARD_WIDTH}px)`,

    [theme.fn.smallerThan('sm')]: {
      flex: `0 0 calc(var(${TRACK_WIDTH_VAR}, 100%) - ${CARD_PEEK}px)`,
    },
  },

  viewAllLink: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.other.spacing._4,
    textDecoration: 'none',

    '&:hover': {
      textDecoration: 'none',
    },
  },

  skeletonRow: {
    display: 'flex',
    gap: theme.other.spacing._8,
    overflow: 'hidden',
  },

  skeletonCard: {
    flex: `0 0 max(calc((100% - ${CARD_PEEK}px - ${
      (CARDS_ON_DESKTOP - 1) * CARD_GAP
    }px) / ${CARDS_ON_DESKTOP}), ${MIN_CARD_WIDTH}px)`,

    [theme.fn.smallerThan('sm')]: {
      flex: `0 0 calc(100% - ${CARD_PEEK}px)`,
    },
  },
}));
